import { z } from 'zod/v4';

export const createUserBonusSchema = z
  .object({
    userId: z.number().int().positive(),
    name: z.string().min(1).max(200),
    type: z.enum(['NUMBER', 'BOOLEAN', 'ARRAY', 'STRING']),
    initialValue: z.union([z.number().min(0), z.boolean(), z.array(z.string()), z.string()]),
    description: z.string().max(1000).nullish(),
    // optional (не default): отсутствие режима — сигнал серверу вывести его
    // из durationDays/expiresAt (admin-панель режим явно не шлёт).
    durationMode: z.enum(['RELATIVE', 'ABSOLUTE', 'INHERIT']).optional(),
    durationDays: z.number().int().positive().nullish(),
    expiresAt: z.string().datetime().nullish(),
  })
  .refine(
    (data) => {
      switch (data.type) {
        case 'NUMBER':
          return typeof data.initialValue === 'number';
        case 'BOOLEAN':
          return typeof data.initialValue === 'boolean';
        case 'ARRAY':
          return Array.isArray(data.initialValue);
        case 'STRING':
          return typeof data.initialValue === 'string';
        default:
          return false;
      }
    },
    {
      message:
        'initialValue must match type: NUMBER→number, BOOLEAN→boolean, ARRAY→string[], STRING→string',
    },
  )
  .refine(
    (data) => {
      if (data.durationMode === 'RELATIVE') {
        return data.durationDays !== null && data.durationDays !== undefined && data.durationDays > 0;
      }
      return true;
    },
    {
      message: 'durationDays is required when durationMode is RELATIVE',
    },
  )
  .refine(
    (data) => {
      if (data.durationMode === 'ABSOLUTE') {
        return data.expiresAt !== null && data.expiresAt !== undefined;
      }
      return true;
    },
    {
      message: 'expiresAt is required when durationMode is ABSOLUTE',
    },
  );

// Действие приходит из admin-панели в верхнем регистре (INCREMENT, TOGGLE, ...).
// Нормализуем к каноническому набору, который понимает сервис: TOGGLE — это
// частный случай set для BOOLEAN, value подставляется в performAction.
export const bonusActionSchema = z
  .enum([
    'increment',
    'decrement',
    'set',
    'toggle',
    'push',
    'remove',
    'INCREMENT',
    'DECREMENT',
    'SET',
    'TOGGLE',
    'PUSH',
    'REMOVE',
  ])
  .transform((v) => v.toLowerCase() as 'increment' | 'decrement' | 'set' | 'toggle' | 'push' | 'remove');

export const updateUserBonusSchema = z.object({
  action: bonusActionSchema,
  // increment/decrement/toggle не требуют value (шаг = 1 / инверсия булева).
  value: z.union([z.number(), z.boolean(), z.string()]).optional(),
  reason: z.string().max(500).nullish(),
  idempotencyKey: z.string().uuid().nullish(),
});

export const revokeUserBonusSchema = z.object({
  reason: z.string().max(500).nullish(),
});

export const userBonusFiltersSchema = z.object({
  status: z.enum(['ACTIVE', 'EXPIRED', 'REVOKED']).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export type CreateUserBonusInput = z.infer<typeof createUserBonusSchema>;
export type UpdateUserBonusInput = z.infer<typeof updateUserBonusSchema>;
export type RevokeUserBonusInput = z.infer<typeof revokeUserBonusSchema>;
export type UserBonusFilters = z.infer<typeof userBonusFiltersSchema>;
